import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { WhatsAppAccount } from '../../../core/services/whatsapp-account.service';
import {
  SetupContext,
  SetupProgress,
  SetupTask,
  setupProgress
} from '../../../core/utils/whatsapp-setup-progress';

/**
 * One honest view of what is left before an organisation can message anyone.
 *
 * Replaces two separate, differently-numbered "getting started" lists — one in
 * the onboarding wizard, one on the dashboard — neither of which mentioned the
 * ownership code, the PIN or the payment method. Those are precisely where
 * people stall, and they happen after the wizard ends, on a page it never
 * pointed at. The result was customers who believed they were live, scheduled
 * a campaign, and watched every message fail.
 *
 * Everything shown is derived on the spot from the account and Meta's own
 * status. Nothing here is stored, because a stored copy would be one more
 * thing to drift out of step with Meta.
 */
@Component({
  selector: 'app-whatsapp-setup-checklist',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './whatsapp-setup-checklist.component.html'
})
export class WhatsappSetupChecklistComponent {
  @Input() account: WhatsAppAccount | null = null;
  @Input() context: SetupContext = {};
  /** Hide the heading where the host page already provides one. */
  @Input() showHeading = true;

  get progress(): SetupProgress {
    return setupProgress(this.account, this.context);
  }

  /** The first thing actually worth doing, so the page can lead with it. */
  get nextTask(): SetupTask | null {
    return this.progress.tasks.find(t => t.state === 'todo') ?? null;
  }

  iconFor(task: SetupTask): string {
    switch (task.state) {
    case 'done': return 'bx-check-circle text-success';
    case 'blocked': return 'bx-lock-alt text-muted';
    case 'unknown': return 'bx-help-circle text-warning';
    default: return 'bx-circle text-muted';
    }
  }

  /**
   * Open Meta Business Manager for the tasks Echo cannot complete itself.
   *
   * Deep-linked to the WABA where possible: "add a payment method" is hard to
   * act on when Business Manager opens on whichever asset it feels like.
   */
  openMetaBilling() {
    const wabaId = this.account?.wabaIds?.[0];
    const businessId = this.account?.businessId;

    let url = 'https://business.facebook.com/latest/settings/whatsapp_account/';
    if (businessId && wabaId) {
      url += `?business_id=${businessId}&selected_asset_id=${wabaId}` +
        '&selected_asset_type=whatsapp-business-account';
    } else if (businessId) {
      url += `?business_id=${businessId}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
